<template>
    <base-form
        ref="form"
        v-model="form"
        :api="api"
        :mapper="mapper"
        :title="title"
        disable-save-button
        @created="created"
        @updated="updated"
        @cancelled="cancelled"
        @deleted="deleted"
    >
        <form-panel>
            <div v-if="!items.length" class="text-center pa-8">
                <v-progress-circular
                    v-if="loading"
                    :size="50"
                    color="primary"
                    indeterminate
                ></v-progress-circular>
                <span v-else>{{ $t('delivery_rules.empty') }}</span>
            </div>
            <div v-else>
                <draggable
                    v-model="items"
                    handle=".handle"
                    @change="orderChanged"
                >
                    <div
                        v-for="(deliveryRule, index) in items"
                        :key="`${index}-${deliveryRule.id}`"
                        class="one-delivery-rule-wrapper"
                    >
                        <div class="handle">
                            <v-icon> drag_indicator</v-icon>
                        </div>
                        <delivery-rule
                            :value="deliveryRule"
                            :index="index"
                            :error-messages="errorMessages"
                            @onDelete="deleteRule"
                            @onChanged="setNeedToSave"
                        ></delivery-rule>
                    </div>
                </draggable>
            </div>
            <div></div>
            <div>
                <v-btn text @click="add"
                    ><v-icon>add</v-icon>{{ $t('labels.add_new') }}</v-btn
                >
            </div>
            <div v-if="needToSave.length || newItem" class="d-flex">
                <v-spacer></v-spacer>
                <v-btn
                    :disabled="!$refs.form.isValid"
                    color="primary"
                    class="white--text"
                    :loading="saveLoading"
                    @click="save"
                    >{{ $t('actions.save') }}</v-btn
                >
            </div>
        </form-panel>
    </base-form>
</template>

<script>
import Draggable from 'vuedraggable'
import FormBase from '@/components/mixins/FormBase'
import DeliveryRule from './DeliveryRule.vue'
import ActiveTabMixin from '@/components/mixins/ActiveTabMixin'
import FormTabMixin from '@/components/mixins/FormTabMixin'
import AuthMixin from '@/components/mixins/AuthMixin'
import CompanyDeliveryRule from '@/api/RestApi/CompanyDeliveryRule'
import ApiClientType from '@/api/RestApiType'
import MapperType from '@/services/mapper/MapperType'
import DirtyForm from '@/mixins/DirtyForm'
import { mapMutations } from 'vuex'
import dirtyType from '@/store/type/dirtyType'
import FormPanel from '@/components/form/FormPanel.vue'
import RestApiCollection from '@/api/RestApiCollection'
import RestApiType from '@/api/RestApiType'

export default {
    components: {
        FormPanel,
        DeliveryRule,
        Draggable,
    },
    mixins: [FormBase, ActiveTabMixin, FormTabMixin, AuthMixin, DirtyForm],
    props: {
        companyId: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            api: ApiClientType.COMPANY_DELIVERY_RULE,
            mapper: MapperType.COMPANY_DELIVERY_RULE_FORM,
            items: [],
            alma: '',
            errorMessages: [],
            needToSave: [],
            newItem: null,
            saveLoading: false,
            loading: true,
        }
    },
    mounted() {
        this.reloadItems()
    },
    methods: {
        ...mapMutations({
            setDirty: dirtyType.mutations.SET_DIRTY,
        }),
        orderChanged() {
            for (let index in this.items) {
                this.items[index].position = Number.parseInt(index)
            }
        },
        async reloadItems() {
            this.items = (
                await CompanyDeliveryRule.list({
                    'order[position]': 'asc',
                })
            )['hydra:member']
            this.needToSave = []
            this.newItem = null
            this.loading = false
        },
        setNeedToSave(changedId) {
            if (this.needToSave.indexOf(changedId) > -1) {
                return
            }
            this.needToSave.push(changedId)
        },
        async save() {
            if (!this.$refs.form.isValid) {
                return false
            }
            this.saveLoading = true

            for (let updatedId of this.needToSave) {
                const updated = this.items.find((x) => x['id'] === updatedId)
                await CompanyDeliveryRule.update(updated, updatedId)
            }
            if (this.newItem) {
                await CompanyDeliveryRule.create(this.newItem)
            }

            await this.reloadItems()
            this.setDirty(false)

            this.saveLoading = false
        },
        async add() {
            const companyApi = RestApiCollection.get(RestApiType.COMPANIES)

            this.newItem = {
                company: await companyApi.getItemIri(this.companyId),
                timeFrom: null,
                timeTo: null,
                pickupDayOffset: null,
                deliveryMethod: null,
                preventInProgressDeliveryMethod: null,
                preventInProgressWeekDays: null,
                onfleetForceOfflineDriverUnassign: false,
                position: this.items.length,
            }
            this.items.push(this.newItem)
            this.setDirty(true)
        },
        async deleteRule(index) {
            if (this.items[index].id) {
                await CompanyDeliveryRule.delete(this.items[index].id)
            }
            this.items.splice(index, 1)
            this.orderChanged()
            for (let i = index; i < this.items.length; i++) {
                this.needToSave.push(this.items[i].id)
            }
            this.save()
        },
    },
}
</script>

<style lang="scss">
.one-delivery-rule-wrapper {
    display: flex;

    .handle {
        display: flex;
        width: 50px;
        cursor: pointer;

        i {
            font-size: 35px;
        }
    }
}
</style>
